import { Check, X, Minus, Sparkles } from "lucide-react";

const COLS = [
    { key: "anand",    title: "Anand Tutorials", subtitle: "1:1 personalised tuition", highlight: true },
    { key: "coaching", title: "Coaching Centres",subtitle: "Large group batches" },
    { key: "apps",     title: "Edtech Apps",     subtitle: "Recorded / automated" },
];

const ROWS = [
    { feature: "1:1 personalised learning",            anand: "yes", coaching: "no",       apps: "no" },
    { feature: "Flexible timings (you pick the slot)", anand: "yes", coaching: "limited",  apps: "yes" },
    { feature: "Verified, background-checked tutors",  anand: "yes", coaching: "limited",  apps: "no" },
    { feature: "Focused attention on your child",      anand: "yes", coaching: "no",       apps: "no" },
    { feature: "Direct parent-tutor communication",    anand: "yes", coaching: "limited",  apps: "no" },
    { feature: "Customised study plan per student",    anand: "yes", coaching: "no",       apps: "no" },
    { feature: "Weekly progress reports",              anand: "yes", coaching: "limited",  apps: "no" },
    { feature: "Free tutor replacement if not a fit",  anand: "yes", coaching: "no",       apps: "no" },
    { feature: "Home tuition (Jabalpur · Bhopal · Nagpur)", anand: "yes", coaching: "limited", apps: "no" },
    { feature: "Global online 1:1 access (NRI / 27 countries)", anand: "yes", coaching: "no", apps: "yes" },
    { feature: "Female tutor on request",              anand: "yes", coaching: "limited",  apps: "no" },
    { feature: "Advance fee protection (never pay tutor directly)", anand: "yes", coaching: "no", apps: "yes" },
];

function Cell({ value }) {
    if (value === "yes")     return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700"><Check className="w-4 h-4" /></span>;
    if (value === "no")      return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-rose-50 text-rose-500"><X className="w-4 h-4" /></span>;
    return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-50 text-amber-600" title="Limited / varies"><Minus className="w-4 h-4" /></span>;
}

export default function ComparisonTable() {
    return (
        <section data-testid="home-comparison" className="py-20 sm:py-24 bg-white">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 text-[10px] uppercase tracking-[0.22em] font-bold text-[#1E3A8A] mb-4">
                        <Sparkles className="w-3 h-3" /> Honest comparison
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight max-w-3xl mx-auto">
                        Anand Tutorials <span className="text-amber-500">vs</span> Coaching Centres <span className="text-amber-500">vs</span> Edtech Apps
                    </h2>
                    <p className="mt-4 text-slate-500 max-w-xl mx-auto text-sm sm:text-base">
                        Before you pick a tutor, compare how we stack up against the two options most parents shortlist.
                    </p>
                </div>

                {/* Desktop table */}
                <div className="hidden md:block bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                    <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr]">
                        <div className="bg-slate-50 p-5 text-xs font-semibold uppercase tracking-wider text-slate-500">What matters most</div>
                        {COLS.map((c) => (
                            <div
                                key={c.key}
                                className={`p-5 text-center border-l border-slate-100 ${c.highlight ? "bg-gradient-to-b from-[#1E3A8A] to-[#1E40AF] text-white" : "bg-slate-50 text-slate-700"}`}
                            >
                                <div className={`font-heading font-bold text-base ${c.highlight ? "text-amber-300" : ""}`}>{c.title}</div>
                                <div className={`text-[11px] mt-1 ${c.highlight ? "text-blue-100/80" : "text-slate-500"}`}>{c.subtitle}</div>
                            </div>
                        ))}
                    </div>
                    {ROWS.map((r, idx) => (
                        <div
                            key={r.feature}
                            className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"} border-t border-slate-100`}
                            data-testid={`comparison-row-${idx}`}
                        >
                            <div className="p-4 text-sm font-medium text-[#0F172A] flex items-center">{r.feature}</div>
                            <div className="p-4 flex items-center justify-center border-l border-slate-100 bg-[#1E3A8A]/[0.03]"><Cell value={r.anand} /></div>
                            <div className="p-4 flex items-center justify-center border-l border-slate-100"><Cell value={r.coaching} /></div>
                            <div className="p-4 flex items-center justify-center border-l border-slate-100"><Cell value={r.apps} /></div>
                        </div>
                    ))}
                </div>

                {/* Mobile stacked */}
                <div className="md:hidden space-y-6">
                    {COLS.map((c) => (
                        <div
                            key={c.key}
                            className={`rounded-2xl border overflow-hidden ${c.highlight ? "border-[#1E3A8A]/40 bg-gradient-to-b from-[#1E3A8A] to-[#1E40AF] text-white" : "border-slate-200 bg-white"}`}
                        >
                            <div className={`p-4 border-b ${c.highlight ? "border-white/15" : "border-slate-100"}`}>
                                <div className={`font-heading font-bold text-lg ${c.highlight ? "text-amber-300" : "text-[#0F172A]"}`}>{c.title}</div>
                                <div className={`text-xs mt-0.5 ${c.highlight ? "text-blue-100/80" : "text-slate-500"}`}>{c.subtitle}</div>
                            </div>
                            <div className={`divide-y ${c.highlight ? "divide-white/10" : "divide-slate-100"}`}>
                                {ROWS.map((r) => (
                                    <div key={r.feature} className="flex items-center justify-between gap-3 p-3">
                                        <span className={`text-sm ${c.highlight ? "text-blue-50" : "text-[#0F172A]"}`}>{r.feature}</span>
                                        <Cell value={r[c.key]} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 text-center text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Included</span>
                    <span className="mx-3">·</span>
                    <span className="inline-flex items-center gap-1"><Minus className="w-3 h-3 text-amber-600" /> Limited / varies</span>
                    <span className="mx-3">·</span>
                    <span className="inline-flex items-center gap-1"><X className="w-3 h-3 text-rose-500" /> Not typically offered</span>
                </div>
            </div>
        </section>
    );
}
